import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { createCmtsCanonicalError } from "../api/cmtsApiErrors.js";
import { mapMasterOption, mapMasterSchema } from "../mappers/masterDataMappers.js";
import { validateMasterDataRecord } from "../validators/masterDataValidators.js";
import { repositoryCall } from "./repositoryResult.js";
import { mapCollection } from "./repositoryCollection.js";
import { optionalCmtsGuid, requireCmtsGuid } from "../utils/cmtsGuid.js";
import { canonicalMasterTypeForGenericApi } from "../master-data/cmtsSupportedMasterTypes.js";


function tenantScopedPayload(payload: Record<string, unknown>, tenantId: string | undefined, options?: { readonly removeId?: boolean }): Record<string, unknown> {
  const next: Record<string, unknown> = { ...payload };
  if (tenantId !== undefined) next.tenantId = tenantId;
  if (options?.removeId) delete next.id;
  return next;
}

function stripMasterCrudTechnicalId(payload: Record<string, unknown>): Record<string, unknown> {
  const { id: _discardedId, ...businessPayload } = payload;
  return businessPayload;
}

function normalizeRepositoryMasterType(masterType: string): string {
  return canonicalMasterTypeForGenericApi(masterType);
}

function masterScopedPayload(masterType: string, payload: Record<string, unknown>, tenantId: string | undefined, options?: { readonly removeId?: boolean }): Record<string, unknown> {
  return tenantScopedPayload({ ...payload, masterType, group: masterType }, tenantId, options);
}

function requireMasterCrudGuid(id: string | undefined, operation: string): string {
  return requireCmtsGuid(id, `Master data ${operation}`);
}

function assertValidMasterDataPayload(payload: Record<string, unknown>): void {
  const diagnostics = validateMasterDataRecord(payload);
  if (!diagnostics.length) return;

  throw createCmtsCanonicalError({
    code: "CMTS_ERR_FAILED_VALIDATION",
    message: "Master data validation failed.",
    details: {
      errors: diagnostics.map((item) => ({
        code: item.code,
        field: item.field,
        message: item.message,
      })),
    },
    raw: diagnostics,
  });
}

export function createMasterDataRepository(client: CmtsHttpClient) {
  return {
    registry: (tenantId?: string) => repositoryCall(
      () => client.get<unknown[]>(CMTS_API.masters.registry, { query: { tenantId } }),
      "CMTS_MASTER_REGISTRY",
    ),
    list: (masterType: string, tenantId?: string) => repositoryCall(
      async () => {
        const canonicalMasterType = normalizeRepositoryMasterType(masterType);
        return mapCollection(await client.get<unknown>(CMTS_API.masters.list(canonicalMasterType), { query: { tenantId } }), mapMasterOption);
      },
      "CMTS_MASTER_LIST",
    ),
    schema: (masterType: string, tenantId?: string) => repositoryCall(
      async () => {
        const canonicalMasterType = normalizeRepositoryMasterType(masterType);
        return mapMasterSchema(await client.get<unknown>(CMTS_API.masters.schema(canonicalMasterType), { query: { tenantId } }), canonicalMasterType);
      },
      "CMTS_MASTER_SCHEMA",
    ),
    dropdown: (masterType: string, tenantId?: string) => repositoryCall(
      async () => {
        const canonicalMasterType = normalizeRepositoryMasterType(masterType);
        return mapCollection(await client.get<unknown>(CMTS_API.masters.dropdown(canonicalMasterType), { query: { tenantId } }), mapMasterOption);
      },
      "CMTS_MASTER_DROPDOWN",
    ),
    dependencyCheck: (masterType: string, id: string, tenantId?: string) => repositoryCall(
      () => {
        const canonicalMasterType = normalizeRepositoryMasterType(masterType);
        return client.get<unknown>(CMTS_API.masters.dependencyCheck(canonicalMasterType, id), { query: { tenantId } });
      },
      "CMTS_MASTER_DEPENDENCY_CHECK",
    ),
    create: (masterType: string, payload: Record<string, unknown>, tenantId?: string) => repositoryCall(async () => {
      const canonicalMasterType = normalizeRepositoryMasterType(masterType);
      const body = masterScopedPayload(canonicalMasterType, payload, tenantId, { removeId: true });
      assertValidMasterDataPayload(body);
      return client.post<unknown>(CMTS_API.masters.list(canonicalMasterType), body, { query: { tenantId } });
    }, "CMTS_MASTER_CREATE"),
    update: (masterType: string, id: string, payload: Record<string, unknown>, tenantId?: string) => repositoryCall(async () => {
      const canonicalMasterType = normalizeRepositoryMasterType(masterType);
      const backendId = requireMasterCrudGuid(id, "update");
      const body = masterScopedPayload(canonicalMasterType, stripMasterCrudTechnicalId(payload), tenantId, { removeId: true });
      assertValidMasterDataPayload(body);
      return client.put<unknown>(CMTS_API.masters.item(canonicalMasterType, backendId), body, { query: { tenantId } });
    }, "CMTS_MASTER_UPDATE"),
    save: (masterType: string, payload: Record<string, unknown>, id: string | undefined, tenantId?: string) => repositoryCall(async () => {
      const canonicalMasterType = normalizeRepositoryMasterType(masterType);
      const suppliedId = id ?? (typeof payload.id === "string" ? payload.id : undefined);
      const backendId = optionalCmtsGuid(suppliedId);
      if (suppliedId && !backendId) throw new Error("Master data save requires a backend GUID id when updating; non-GUID business codes are never treated as technical ids.");
      if (backendId) {
        const body = masterScopedPayload(canonicalMasterType, stripMasterCrudTechnicalId(payload), tenantId, { removeId: true });
        assertValidMasterDataPayload(body);
        return client.put<unknown>(CMTS_API.masters.item(canonicalMasterType, backendId), body, { query: { tenantId } });
      }
      const body = masterScopedPayload(canonicalMasterType, payload, tenantId, { removeId: true });
      assertValidMasterDataPayload(body);
      return client.post<unknown>(CMTS_API.masters.list(canonicalMasterType), body, { query: { tenantId } });
    }, "CMTS_MASTER_SAVE"),
    activate: (masterType: string, id: string, reason: string, tenantId?: string) => repositoryCall(
      () => {
        const canonicalMasterType = normalizeRepositoryMasterType(masterType);
        const backendId = requireMasterCrudGuid(id, "activate");
        return client.patch<unknown>(CMTS_API.masters.activate(canonicalMasterType, backendId), { reason, tenantId }, { query: { tenantId } });
      },
      "CMTS_MASTER_ACTIVATE",
    ),
    deactivate: (masterType: string, id: string, reason: string, tenantId?: string) => repositoryCall(
      () => {
        const canonicalMasterType = normalizeRepositoryMasterType(masterType);
        const backendId = requireMasterCrudGuid(id, "deactivate");
        return client.patch<unknown>(CMTS_API.masters.deactivate(canonicalMasterType, backendId), { reason, tenantId }, { query: { tenantId } });
      },
      "CMTS_MASTER_DEACTIVATE",
    ),
    softDelete: (masterType: string, id: string, reason: string, tenantId?: string) => repositoryCall(
      () => {
        const canonicalMasterType = normalizeRepositoryMasterType(masterType);
        const backendId = requireMasterCrudGuid(id, "delete");
        return client.delete<unknown>(CMTS_API.masters.delete(canonicalMasterType, backendId), { query: { tenantId, reason } });
      },
      "CMTS_MASTER_SOFT_DELETE",
    ),
  };
}
